'use client';

/**
 * Lazy wrapper for `SentryErrorBoundary` (BS-2161).
 *
 * Sentry's React bindings (~250 KB raw) are bundled with the boundary
 * component. Importing `SentryErrorBoundary` directly in the root layout
 * pulls them into First Load JS for every route - even routes that never
 * throw and `/` / `/_not-found` which don't render the boundary's expensive
 * subtree.
 *
 * Strategy: render children unwrapped during the synchronous render pass
 * (so the bundle does not block hydration), then swap in the real Sentry
 * boundary after a `useEffect`-triggered dynamic import resolves. Any
 * uncaught errors thrown before the swap fall through to
 * `app/global-error.tsx`, which is unchanged.
 *
 * Sentry init itself still runs eagerly via `instrumentation-client.ts`
 * (Next.js loads it independently), so the brief unwrapped window does NOT
 * lose error capture - `Sentry.captureException` calls inside the React
 * tree still work; only the React error-boundary fallback UI is deferred.
 */

import { useEffect, useState, type ComponentType, type ReactNode } from 'react';

type BoundaryComponent = ComponentType<{ children: ReactNode }>;

interface SentryErrorBoundaryProps {
  children: ReactNode;
}

export function SentryErrorBoundary({ children }: SentryErrorBoundaryProps) {
  const [Boundary, setBoundary] = useState<BoundaryComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('./sentry-error-boundary').then((mod) => {
      if (!cancelled) {
        setBoundary(() => mod.SentryErrorBoundary as BoundaryComponent);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!Boundary) {
    // First paint: render children unwrapped. Errors fall through to
    // app/global-error.tsx during this window.
    return <>{children}</>;
  }

  return <Boundary>{children}</Boundary>;
}
